import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import ToolkitProvider, { CSVExport } from "react-bootstrap-table2-toolkit";
import { Card, CardBody } from "reactstrap";
import { FAILED_VOICEMAIL_NOTE_DESCRIPTION, MPC_COPY } from "./monthlyPhoneConsultationLabels";

const { ExportCSVButton } = CSVExport;

const FAILED_PHONE_ATTEMPTS_COLUMN_LABEL = "Failed Phone Attempts This Month";
const FAILED_PHONE_ATTEMPTS_COLUMN_TOOLTIP =
  `Total unsuccessful outreach attempts (${FAILED_VOICEMAIL_NOTE_DESCRIPTION}) in the selected month. Color scale: 1-2 attempts = yellow, 3+ attempts = red.`;

const CALL_ATTEMPTS_TO_CONSULTATION_COLUMN_LABEL = "Calls to Consultation";
const AVERAGE_ATTEMPTS_BEFORE_CONSULTATION_COLUMN_LABEL = "Average Consultation Success Rate";
const AVERAGE_ATTEMPTS_BEFORE_CONSULTATION_COLUMN_TOOLTIP =
  "Per-patient consultation success percentage based on failed + successful attempts. 100.0% means no failed attempts. Color scale: 100.0% = green, 50-99.9% = yellow, below 50% = red.";

const HeaderWithTooltip = ({ label, tooltip, tone = "neutral" }) => {
  const anchorRef = useRef(null);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });

  const updateTooltipPosition = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) {
      return;
    }

    const rect = anchor.getBoundingClientRect();
    setTooltipPosition({
      top: rect.bottom + 8,
      left: rect.left + rect.width / 2
    });
  }, []);

  const showTooltip = useCallback(() => {
    updateTooltipPosition();
    setIsTooltipVisible(true);
  }, [updateTooltipPosition]);

  const hideTooltip = useCallback(() => {
    setIsTooltipVisible(false);
  }, []);

  useEffect(() => {
    if (!isTooltipVisible) {
      return undefined;
    }

    const handleDismiss = () => hideTooltip();
    window.addEventListener("scroll", handleDismiss, true);
    window.addEventListener("resize", handleDismiss);

    return () => {
      window.removeEventListener("scroll", handleDismiss, true);
      window.removeEventListener("resize", handleDismiss);
    };
  }, [hideTooltip, isTooltipVisible]);

  return (
    <>
      <span className={`mpc-header-with-tooltip mpc-header-with-tooltip--${tone}`}>
        <span>{label}</span>
        <span
          ref={anchorRef}
          className="mpc-header-tooltip-anchor"
          tabIndex={0}
          aria-label={`${label} info`}
          onMouseEnter={showTooltip}
          onMouseLeave={hideTooltip}
          onFocus={showTooltip}
          onBlur={hideTooltip}
        >
          <i className="mdi mdi-information-outline mpc-header-tooltip-icon" aria-hidden="true" />
        </span>
      </span>
      {isTooltipVisible && typeof document !== "undefined"
        ? createPortal(
          <span
            role="tooltip"
            className={`mpc-header-tooltip-bubble mpc-header-tooltip-bubble--portal mpc-header-tooltip-bubble--${tone}`}
            style={{
              position: "fixed",
              top: tooltipPosition.top,
              left: tooltipPosition.left,
              transform: "translateX(-50%)"
            }}
          >
            {tooltip}
          </span>,
          document.body
        )
        : null}
    </>
  );
};

const FailedPhoneAttemptsHeader = () => (
  <HeaderWithTooltip
    label={FAILED_PHONE_ATTEMPTS_COLUMN_LABEL}
    tooltip={FAILED_PHONE_ATTEMPTS_COLUMN_TOOLTIP}
    tone="failed"
  />
);

const capitalizeWord = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

const formatPatientName = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "-";

  if (raw.includes(",")) {
    const [lastName, firstName] = raw.split(",").map((part) => part.trim());
    return [firstName, lastName]
      .filter(Boolean)
      .map((part) => part.split(/\s+/).map(capitalizeWord).join(" "))
      .join(" ");
  }

  return raw
    .split(/\s+/)
    .map(capitalizeWord)
    .join(" ");
};

const getProfileImageSrc = (profileImage) => {
  if (!profileImage) return "";
  if (String(profileImage).startsWith("http")) return profileImage;
  return `${process.env.REACT_APP_VIDEO_CALL_REQUEST}/${profileImage}`;
};

const getOrganizationIcon = (organization) => {
  if (!organization || organization === "-") return <i className="mdi mdi-hospital-building" />;
  const orgLower = String(organization).toLowerCase();
  if (orgLower.includes("hospital") || orgLower.includes("medical")) {
    return <i className="mdi mdi-hospital-building" />;
  }
  if (orgLower.includes("clinic")) {
    return <i className="mdi mdi-medical-bag" />;
  }
  if (orgLower.includes("health") || orgLower.includes("wellness")) {
    return <i className="mdi mdi-heart-pulse" />;
  }
  if (orgLower.includes("care")) {
    return <i className="mdi mdi-hand-heart" />;
  }
  return <i className="mdi mdi-domain" />;
};

const PatientProfileCell = ({ patientName, profileImage, email, phone, isActive, onViewPatient, row }) => {
  const [imageError, setImageError] = useState(false);
  const imageSrc = getProfileImageSrc(profileImage);
  const showImage = Boolean(imageSrc) && !imageError;

  return (
    <div className="mpr-patient-profile-cell">
      <div className="mpr-patient-profile-avatar-wrap">
        {showImage ? (
          <img
            src={imageSrc}
            alt={patientName}
            className="mpr-patient-profile-avatar"
            onError={() => setImageError(true)}
          />
        ) : (
          <span className="mpr-patient-profile-fallback-icon">
            <i className="mdi mdi-account" />
          </span>
        )}
      </div>
      <div className="mpr-patient-profile-content">
        <div className="mpr-patient-profile-name-row">
          {onViewPatient ? (
            <button type="button" className="mpc-patient-link" onClick={() => onViewPatient(row)}>
              {patientName}
            </button>
          ) : (
            <div className="mpr-patient-profile-name">{patientName}</div>
          )}
          <span className={`mpr-patient-active-tag ${isActive ? "active" : "inactive"}`}>
            {isActive ? "Active" : "Inactive"}
          </span>
        </div>
        <div className="mpr-patient-profile-meta">
          <i className="mdi mdi-email-outline" />
          <span>{email || "-"}</span>
        </div>
        <div className="mpr-patient-profile-meta">
          <i className="mdi mdi-phone-outline" />
          <span>{phone || "-"}</span>
        </div>
      </div>
    </div>
  );
};

const normalizePatients = (patients = []) =>
  patients.map((patient) => {
    const normalizedAverageAttempts = Number(patient.averageAttemptsBeforeConsultation);
    const normalizedCallAttempts = Number(patient.callAttemptsToConsultation);

    return {
      ...patient,
      _id: patient._id || `${patient.email}-${patient.phone}`,
      patientName: formatPatientName(patient.patientName),
      providerName: patient.mainCareProvider?.name || "-",
      organization: patient.client?.organization || "-",
      monthlyPhoneNotesCount: Number(patient.totalConsultationCount ?? patient.monthlyPhoneNotesCount) || 0,
      monthlyPhoneNotes: Array.isArray(patient.monthlyPhoneNotes) ? patient.monthlyPhoneNotes : [],
      failedPhoneAttemptsThisMonth: Number(patient.failedPhoneAttemptsThisMonth) || 0,
      failedPhoneNotes: Array.isArray(patient.failedPhoneNotes) ? patient.failedPhoneNotes : [],
      successfulConsultationCount: Number(patient.successfulConsultationCount) || 0,
      successfulConsultationNotes: Array.isArray(patient.successfulConsultationNotes)
        ? patient.successfulConsultationNotes
        : [],
      averageAttemptsBeforeConsultation: Number.isFinite(normalizedAverageAttempts)
        ? normalizedAverageAttempts
        : Number(patient.failedPhoneAttemptsThisMonth) || 0,
      callAttemptsToConsultation: Number.isFinite(normalizedCallAttempts)
        ? normalizedCallAttempts
        : Number(patient.successfulConsultationCount) || 0,
      isActive: Boolean(patient.isActive)
    };
  }).sort((a, b) =>
    String(a.patientName || "").localeCompare(String(b.patientName || ""), undefined, {
      sensitivity: "base"
    })
  );

const PatientListSection = ({
  title,
  description,
  patients,
  showNotesColumn = false,
  showFailedAttemptsColumn = false,
  showAverageAttemptsBeforeConsultationColumn = false,
  showCallAttemptsColumn = false,
  fileSuffix,
  onViewPatient,
  onViewNotes,
  onViewFailedAttempts,
  onViewSuccessfulConsultations,
  listIcon = "mdi-account-group-outline",
  embedded = false,
  mpcCopy = MPC_COPY
}) => {
  const [searchText, setSearchText] = useState("");
  const rows = useMemo(() => normalizePatients(patients), [patients]);

  const filteredRows = useMemo(() => {
    const query = searchText.toLowerCase().trim();
    if (!query) return rows;
    return rows.filter((row) =>
      [row.patientName, row.email, row.phone, row.providerName, row.organization]
        .map((v) => String(v || "").toLowerCase())
        .some((v) => v.includes(query))
    );
  }, [rows, searchText]);

  const columns = useMemo(() => {
    const base = [
      {
        dataField: "patientName",
        text: "Patient",
        sort: true,
        formatter: (cell, row) => (
          <PatientProfileCell
            patientName={cell}
            profileImage={row.profileImage}
            email={row.email}
            phone={row.phone}
            isActive={row.isActive}
            onViewPatient={onViewPatient}
            row={row}
          />
        ),
        headerStyle: () => ({ minWidth: "280px" })
      },
      { dataField: "providerName", text: "Provider", sort: true },
      {
        dataField: "organization",
        text: "Organization",
        sort: true,
        formatter: (cell) => (
          <div className="mpr-org-cell-wrapper">
            <span className="mpr-org-icon">{getOrganizationIcon(cell)}</span>
            <span className="mpr-org-text">{cell}</span>
          </div>
        )
      },
    ];

    if (showNotesColumn) {
      base.push({
        dataField: "monthlyPhoneNotesCount",
        text: mpcCopy.consultationsColumnLabel,
        headerFormatter: () => (
          <HeaderWithTooltip
            label={mpcCopy.consultationsColumnLabel}
            tooltip={mpcCopy.consultationsColumnTooltip}
            tone="neutral"
          />
        ),
        sort: true,
        headerStyle: () => ({ minWidth: "170px", whiteSpace: "nowrap" }),
        formatter: (cell, row) => {
          const count = Number(cell) || 0;
          if (count === 0) {
            return <span className="mpc-muted-dash">—</span>;
          }
          return (
            <button
              type="button"
              className="mpc-notes-count-btn"
              onClick={() => onViewNotes && onViewNotes(row)}
              title="View all consultation activity"
            >
              {count}
              <i className="mdi mdi-chevron-right" />
            </button>
          );
        }
      });
    } else {
      base.push({
        dataField: "monthlyPhoneNotesCount",
        text: "Status",
        sort: true,
        sortValue: (cell) => Number(cell) || 0,
        formatter: () => (
          <span className="mpr-badge mpr-status-badge no">{mpcCopy.noConsultationStatus}</span>
        )
      });
    }

    if (showFailedAttemptsColumn) {
      base.push({
        dataField: "failedPhoneAttemptsThisMonth",
        text: FAILED_PHONE_ATTEMPTS_COLUMN_LABEL,
        headerFormatter: FailedPhoneAttemptsHeader,
        sort: true,
        headerStyle: () => ({ minWidth: "220px", whiteSpace: "normal", lineHeight: "1.25" }),
        formatter: (cell, row) => {
          const count = Number(cell) || 0;
          if (count === 0) {
            return (
              <div className="mpc-count-cell">
                <span className="mpc-muted-dash">0</span>
              </div>
            );
          }
          return (
            <div className="mpc-count-cell">
              <button
                type="button"
                className={`mpc-failed-pill ${count <= 2 ? "low" : "high"} mpc-failed-pill-btn`}
                onClick={() => onViewFailedAttempts && onViewFailedAttempts(row)}
                title={FAILED_PHONE_ATTEMPTS_COLUMN_TOOLTIP}
              >
                {count}
                <i className="mdi mdi-chevron-right" />
              </button>
            </div>
          );
        }
      });
    }

    if (showCallAttemptsColumn) {
      base.push({
        dataField: "callAttemptsToConsultation",
        text: CALL_ATTEMPTS_TO_CONSULTATION_COLUMN_LABEL,
        headerFormatter: () => (
          <HeaderWithTooltip
            label={CALL_ATTEMPTS_TO_CONSULTATION_COLUMN_LABEL}
            tooltip={mpcCopy.callsToConsultationTooltip}
            tone="success"
          />
        ),
        sort: true,
        headerStyle: () => ({ minWidth: "160px", whiteSpace: "normal", lineHeight: "1.25" }),
        formatter: (cell, row) => {
          const count = Number(cell) || 0;
          if (count === 0) {
            return (
              <div className="mpc-count-cell">
                <span className="mpc-muted-dash">0</span>
              </div>
            );
          }
          return (
            <div className="mpc-count-cell">
              <button
                type="button"
                className="mpc-notes-count-btn mpc-notes-count-btn--success"
                onClick={() => onViewSuccessfulConsultations && onViewSuccessfulConsultations(row)}
                title="View documented consultation details"
              >
                {count}
                <i className="mdi mdi-chevron-right" />
              </button>
            </div>
          );
        }
      });
    }

    if (showAverageAttemptsBeforeConsultationColumn) {
      base.push({
        dataField: "averageAttemptsBeforeConsultation",
        text: AVERAGE_ATTEMPTS_BEFORE_CONSULTATION_COLUMN_LABEL,
        headerFormatter: () => (
          <HeaderWithTooltip
            label={AVERAGE_ATTEMPTS_BEFORE_CONSULTATION_COLUMN_LABEL}
            tooltip={AVERAGE_ATTEMPTS_BEFORE_CONSULTATION_COLUMN_TOOLTIP}
            tone="average"
          />
        ),
        sort: true,
        headerStyle: () => ({ minWidth: "230px", whiteSpace: "normal", lineHeight: "1.25" }),
        formatter: (cell) => {
          const percentage = Number(cell) || 0;
          const toneClass = percentage >= 100 ? "success" : percentage >= 50 ? "low" : "high";
          return (
            <div className="mpc-count-cell">
              <span className={`mpc-failed-pill ${toneClass}`}>{`${percentage.toFixed(1)}%`}</span>
            </div>
          );
        }
      });
    }

    return base;
  }, [
    showNotesColumn,
    showFailedAttemptsColumn,
    showAverageAttemptsBeforeConsultationColumn,
    showCallAttemptsColumn,
    onViewPatient,
    onViewNotes,
    onViewFailedAttempts,
    onViewSuccessfulConsultations,
    mpcCopy
  ]);

  const customTotal = (from, to, size) => (
    <span className="react-bootstrap-table-pagination-total">
      Showing {from} to {to} of {size} patients
    </span>
  );

  const paginationOptions = useMemo(() => ({
    bootstrap4: true,
    sizePerPage: 10,
    totalSize: filteredRows.length,
    page: 1,
    firstPageText: "First",
    prePageText: "Back",
    nextPageText: "Next",
    lastPageText: "Last",
    alwaysShowAllBtns: true,
    withFirstAndLast: true,
    hideSizePerPage: false,
    hidePageListOnlyOnePage: true,
    showTotal: filteredRows.length > 0,
    paginationTotalRenderer: customTotal,
    sizePerPageList: [
      { text: "10", value: 10 },
      { text: "25", value: 25 },
      { text: "50", value: 50 }
    ]
  }), [filteredRows.length]);

  const paginationTableKey = useMemo(
    () => `${searchText.trim().toLowerCase()}|${filteredRows.length}`,
    [searchText, filteredRows.length]
  );

  return (
    <Card className={`mpc-table-card mpr-patient-list-section ${embedded ? "mpc-table-card-embedded" : ""}`}>
      <CardBody className={embedded ? "mpc-tab-panel-body" : undefined}>
        {embedded ? (
          <div className="mpc-panel-intro">
            <div className="mpc-panel-intro-row">
              <div className="mpc-panel-intro-copy">
                <h4>{title}</h4>
                <p>{description}</p>
              </div>
              <span className="mpc-list-count-badge">{filteredRows.length} patients</span>
            </div>
          </div>
        ) : (
          <div className="chart-header-modern mpc-list-header">
            <div className={`chart-icon-modern ${showNotesColumn ? "success" : "alert"}`}>
              <i className={`mdi ${listIcon}`} />
            </div>
            <div className="chart-header-copy-modern">
              <h4>{title}</h4>
              <p>{description}</p>
            </div>
            <span className="mpc-list-count-badge">{filteredRows.length} patients</span>
          </div>
        )}

        <div className="mpr-patient-card mpc-patient-table-wrap">
          <ToolkitProvider
            keyField="_id"
            data={filteredRows}
            columns={columns}
            exportCSV={{
              fileName: `${fileSuffix}.csv`,
              exportAll: true,
              onlyExportFiltered: true
            }}
          >
            {(toolkitProps) => (
              <>
                <div className="mpr-patient-toolbar patient-list-toolbar">
                  <div className="mpr-search-wrap patient-list-search-shell">
                    <span className="patient-list-search-icon">
                      <i className="mdi mdi-magnify" />
                    </span>
                    <input
                      type="text"
                      className="patient-list-search-input"
                      placeholder="Search patient, email, provider, organization..."
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                    />
                    {searchText && (
                      <button
                        type="button"
                        className="patient-list-search-clear"
                        onClick={() => setSearchText("")}
                        aria-label="Clear search"
                      >
                        <i className="mdi mdi-close-circle" />
                      </button>
                    )}
                  </div>
                  {filteredRows.length > 0 && (
                    <ExportCSVButton
                      {...toolkitProps.csvProps}
                      className="mpr-export-btn patient-list-export-btn"
                    >
                      <span className="patient-list-export-icon">
                        <i className="mdi mdi-file-export-outline" />
                      </span>
                      <span>Export CSV</span>
                    </ExportCSVButton>
                  )}
                </div>
                <div className="mpc-table-scroll-shell">
                  <BootstrapTable
                    key={paginationTableKey}
                    {...toolkitProps.baseProps}
                    bootstrap4
                    bordered={false}
                    wrapperClasses="table-responsive mpc-table-responsive"
                    classes="mpr-patient-table mpc-patient-table"
                    pagination={paginationFactory(paginationOptions)}
                    noDataIndication={() => (
                      <div className="mpc-empty-state">
                        <i className="mdi mdi-account-search-outline" />
                        <p>No patients in this list for the selected period.</p>
                      </div>
                    )}
                  />
                </div>
              </>
            )}
          </ToolkitProvider>
        </div>
      </CardBody>
    </Card>
  );
};

export default PatientListSection;
